import { useEffect, useState } from "react";
import { Loader2, CalendarCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const AttendanceTab = () => {
  const [phone, setPhone] = useState("");
  const [checking, setChecking] = useState(false);
  const [todayList, setTodayList] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadToday = async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setTodayList(data.entries || []);
    } catch {
      toast.error("Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadToday(); }, []);

  const checkin = async (e) => {
    e.preventDefault();
    if (!phone.trim() || checking) return;
    setChecking(true);
    try {
      const { data } = await api.post("/attendance/checkin", { phone: phone.trim() });
      const expired = data.computed_status === "expired";
      toast.success(`${data.name} checked in`, {
        description: expired ? "Membership EXPIRED — please renew!" : `${data.package_name_en} • valid till ${data.expiry_date}`,
      });
      setPhone("");
      loadToday();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mt-6" data-testid="attendance-tab">
      <form onSubmit={checkin} className="flex flex-col gap-3 rounded-xl border border-volt/30 bg-surface p-5 sm:flex-row sm:items-center" data-testid="checkin-form">
        <CalendarCheck className="hidden h-6 w-6 shrink-0 text-volt sm:block" />
        <input
          data-testid="checkin-phone-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Member phone number (98XXXXXXXX)"
          inputMode="numeric"
          className="flex-1 rounded-full border border-zinc-700 bg-ink px-5 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-volt"
        />
        <button
          data-testid="checkin-submit-button"
          type="submit"
          disabled={checking}
          className="flex items-center justify-center gap-2 rounded-full bg-volt px-7 py-3 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all hover:bg-volt-hover disabled:opacity-60"
        >
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Check In
        </button>
      </form>

      <div className="mt-5 flex items-center gap-3">
        <span className="rounded-full border border-volt/40 bg-volt/10 px-4 py-1.5 font-display text-sm font-bold uppercase tracking-widest text-volt" data-testid="attendance-count">
          Today: {todayList.length}
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}
        </span>
      </div>

      <div className="mt-4 space-y-2.5" data-testid="attendance-list">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-volt" /></div>
        ) : todayList.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 py-12 text-center text-sm text-zinc-500" data-testid="attendance-empty">No check-ins yet today.</p>
        ) : (
          todayList.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-surface px-5 py-3.5" data-testid={`attendance-entry-${e.id}`}>
              <div className="min-w-0">
                <p className="truncate font-semibold text-white">{e.name}</p>
                <p className="font-mono text-xs text-zinc-500">{e.phone}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {e.computed_status === "expired" && (
                  <span className="flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-red-400">
                    <AlertTriangle className="h-3 w-3" /> Expired
                  </span>
                )}
                <span className="font-mono text-xs text-zinc-400">
                  {new Date(e.time).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AttendanceTab;
